import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Actions,
  VoiceNameContainer,
  VoiceNameContent,
  VoiceUploadContainer,
} from '../../../../components/onboarding/InstantVoiceUpload/InstantVoiceUpload.styles';
import { GenerateButton } from '../EditInfluencerPage.styles';
import { Text14Light, Text14Regular } from '../../../../components/utils/Text/Text.styles';
import { ErrorContainer, Spinner } from '../../../../components';
import { ReactComponent as CloseIcon } from '../../../../assets/icons/close.svg';
import { selectChatterActionsPending } from '../../../../store/slices/chatter/slice';
import {
  refreshInfluencerFactsStatus,
  uploadInfluencerQuestionnaire,
} from '../../../../store/slices/chatter/asyncThunks';
import { notifyError, notifyInfo } from '../../../../utils/notify';

// Must match PDF_FILE_SIZE_LIMIT on the server
const MAX_PDF_SIZE_IN_MB = 30;
const POLL_INTERVAL_MS = 10 * 1000;

const QuestionnaireSection = ({ influencer }) => {
  const dispatch = useDispatch();
  const { uploadInfluencerQuestionnaire: uploadPending } = useSelector(selectChatterActionsPending);
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const { id: influencerId, hasFacts, isFactsQuestionnaireProcessing } = influencer;

  // Job runs in the background, so poll until it finishes (lock released)
  useEffect(() => {
    if (!isFactsQuestionnaireProcessing) return;
    const interval = setInterval(() => {
      dispatch(refreshInfluencerFactsStatus({ influencerId }))
        .unwrap()
        .then(({ influencer: updated }) => {
          if (updated.isFactsQuestionnaireProcessing) return;
          if (updated.hasFacts) notifyInfo('Facts generated from questionnaire!');
          else notifyError('Questionnaire processing failed. Please try again.');
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [dispatch, influencerId, isFactsQuestionnaireProcessing]);

  const onSelectFile = e => {
    const selected = e.target.files?.[0];
    e.target.value = '';
    if (!selected) return;
    setFile(selected);
    if (selected.type !== 'application/pdf') setError('Invalid file type (allowed: PDF)');
    else if (selected.size > MAX_PDF_SIZE_IN_MB * 1024 * 1024)
      setError(`File size is too large (max ${MAX_PDF_SIZE_IN_MB}MB)`);
    else setError(null);
  };

  const onRemoveFile = () => {
    setFile(null);
    setError(null);
  };

  const onUpload = () => {
    dispatch(uploadInfluencerQuestionnaire({ influencerId, file }))
      .unwrap()
      .then(() => {
        setFile(null);
        notifyInfo('Questionnaire uploaded. Facts are being generated.');
      })
      .catch(err => notifyError(err.message));
  };

  if (hasFacts) {
    return <Text14Regular>Facts are already set for this creator.</Text14Regular>;
  }

  if (isFactsQuestionnaireProcessing) {
    return (
      <>
        <Spinner width={40} height={40} />
        <Text14Regular>
          Questionnaire is being processed. This can take a few minutes, you can leave this page.
        </Text14Regular>
      </>
    );
  }

  return (
    <>
      <Text14Regular>
        Upload the creator's filled questionnaire as a PDF (up to {MAX_PDF_SIZE_IN_MB}MB). Facts about the creator will
        be generated from it.
      </Text14Regular>
      <input ref={inputRef} type="file" accept=".pdf,application/pdf" hidden onChange={onSelectFile} />
      {file ? (
        <VoiceUploadContainer>
          <VoiceNameContainer>
            <VoiceNameContent>
              <Text14Light>{file.name}</Text14Light>
              <Actions>
                {!uploadPending && <CloseIcon onClick={onRemoveFile} width={20} height={20} color="#1672BA" />}
              </Actions>
            </VoiceNameContent>
            {error && <ErrorContainer errorText={error} />}
          </VoiceNameContainer>
        </VoiceUploadContainer>
      ) : (
        <GenerateButton title="Choose PDF" onClick={() => inputRef.current?.click()} type="button" />
      )}
      {file && (
        <GenerateButton title="Upload" onClick={onUpload} disabled={!!error} isLoading={uploadPending} type="button" />
      )}
    </>
  );
};

export default QuestionnaireSection;
